/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useRef, useState } from 'react';
// https://github.com/akiran/react-slick
import type { Settings } from 'react-slick';
import Slider from 'react-slick';

import { breakpoints } from '@coursera/cds-core';

import { updateSlickAccessibilityAttributes } from 'bundles/design-system/utils/react-slick-utils';
import HeroCarouselControlButton from 'bundles/front-page/components/modules/hero-banner/HeroCarouselControlButton';
import {
  CAROUSEL_SLIDE_HEIGHT,
  MOBILE_IMAGE_HEIGHT,
} from 'bundles/front-page/components/modules/hero-banner/HeroCarouselSlide';

import _t from 'i18n!nls/front-page';

const styles = {
  heroCarousel: css`
    padding: 0;
    width: 100%;

    .slick-prev,
    .slick-next {
      position: absolute;
      cursor: pointer;
      z-index: 1;
      top: 0;
      height: ${CAROUSEL_SLIDE_HEIGHT}px;
    }

    .slick-track {
      position: relative;
    }

    .slick-prev {
      left: calc(50% - 700px);
    }

    .slick-next {
      right: calc(50% - 700px);
    }

    ${breakpoints.down('md')} {
      .slick-prev {
        left: 12px;
      }

      .slick-next {
        right: 12px;
      }

      .slick-prev,
      .slick-next {
        height: fit-content;
        top: calc(${MOBILE_IMAGE_HEIGHT}px - 32px);
      }
    }

    ${breakpoints.down('xs')} {
      .slick-prev {
        left: 6px;
      }

      .slick-next {
        right: 6px;
      }
    }

    .slick-slider {
      height: ${CAROUSEL_SLIDE_HEIGHT}px;
      margin: auto;
      position: relative;
      display: block;
      box-sizing: border-box;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      -khtml-user-select: none;
      -ms-touch-action: pan-y;
      touch-action: pan-y;
      -webkit-tap-highlight-color: transparent;

      ${breakpoints.down('md')} {
        height: auto;
      }
    }

    .slick-list {
      height: auto;
      position: relative;
      display: block;
      overflow: hidden;
      transition: height 500ms;
      padding: 16px;

      ${breakpoints.up('md')} {
        height: ${CAROUSEL_SLIDE_HEIGHT}px !important;
      }

      &:focus {
        outline: none;
      }
    }

    .slick-loading {
      .slick-track,
      .slick-slide {
        visibility: hidden;
      }
    }

    .slick-slide {
      display: none;
      float: left;
      min-height: 1px;

      img {
        display: block;
      }

      &.slick-loading {
        img {
          display: none;
        }
      }

      &.dragging {
        img {
          pointer-events: none;
        }
      }
    }

    .slick-initialized {
      .slick-slide,
      section {
        display: block;
        padding-top: 0;
      }
    }

    .slick-dots {
      position: relative;
      display: inline-flex !important;
      gap: 16px;
      list-style: none;
      width: 100%;
      justify-content: center;
      padding-left: 0;
      margin-top: var(--cds-spacing-50);

      ${breakpoints.up('md')} {
        margin-top: calc((var(--cds-spacing-400) + var(--cds-spacing-50)) * -1);
        position: absolute;
      }

      button {
        display: block;
        width: 20px;
        height: 20px;
        padding: 0;
        border: none;
        border-radius: 100%;
        background-color: var(--cds-color-neutral-stroke-primary-weak);
        text-indent: 100%;
        white-space: nowrap;
        overflow: hidden;
      }

      li.slick-active button {
        background-color: var(--cds-color-neutral-stroke-primary);
      }
    }
  `,
};

type Props = {
  slides?: JSX.Element[];
};

const HeroCarousel = ({ slides }: Props) => {
  const element = useRef(null);
  const currentSlideIndex = useRef(0);
  const [currentSlide, setCurrentSlide] = useState(1);

  const handlePageChange = useCallback(
    (current) => {
      currentSlideIndex.current = current;
      let updateCurrentSlide: number;
      if (current === 0) {
        updateCurrentSlide = 1;
      } else {
        updateCurrentSlide = currentSlide + 1;
      }

      updateSlickAccessibilityAttributes(element.current);
      setCurrentSlide(updateCurrentSlide);
    },
    [currentSlideIndex, currentSlide]
  );
  useEffect(() => {
    if (element) {
      updateSlickAccessibilityAttributes(element.current);
    }
  }, [element]);

  const settings = {
    dots: true,
    infinite: true,
    autoplaySpeed: 4000,
    speed: 1000,
    autoplay: true,
    centerMode: true,
    centerPadding: '0',
    initialSlide: 0,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnHover: true,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
    waitForAnimate: true,
    arrows: true,
    nextArrow: <HeroCarouselControlButton currentRef={currentSlideIndex} isNext />,
    prevArrow: <HeroCarouselControlButton currentRef={currentSlideIndex} />,
    cssEase: 'ease-in-out',
    draggable: false,
    afterChange: handlePageChange,
    responsive: [
      {
        breakpoint: breakpoints.values.md,
        settings: {
          adaptiveHeight: true, // possibly causing strange timing/behaviour on mobile
        },
      },
    ],
  } as Settings;

  if (!slides) {
    return null;
  }

  return (
    <section css={styles.heroCarousel} data-testid="lohp-hero-carousel" ref={element}>
      {slides.length > 1 ? <Slider {...settings}>{slides}</Slider> : slides[0]}
      <div aria-live="polite" aria-atomic="true" aria-relevant="text" className="screenreader-only">
        {_t('You are currently on slide #{currentSlide}', { currentSlide })}
      </div>
    </section>
  );
};

export default HeroCarousel;
